/**
 * KB Semantic Mapper (Layer 2 Governance)
 * 
 * Objective: Normalize ALL entries (legacy & new) to a single schema.
 * Input: Raw DB Entry
 * Output: Semantic Definition
 * 
 * Rules:
 * - No grandfathering.
 * - Nulls are explicit nulls.
 * - Text-based scores ("GREEN") are converted to visual_score semantics or null if ambiguous.
 * - Recommendation is normalized to SAVE, DISCARD, TRACK.
 */

export const REFERENCE_SCORING = {
    GREEN: { min: 80, label: 'High Confidence' }, // noscan
    YELLOW: { min: 50, label: 'Medium Confidence' }, // noscan
    RED: { min: 0, label: 'Low Confidence' } // noscan
};

export const normalizeEntry = (entry) => {
    if (!entry) return null;

    // 1. Score Normalization
    console.log("[KB-MAPPER] Normalizing:", entry.id, entry.title);
    // Preference: hard_metric_score (Number) > visual_score (String Mapping) > null
    let normalizedScore = null;

    if (typeof entry.hard_metric_score === 'number') {
        normalizedScore = entry.hard_metric_score;
    } else if (entry.visual_score) {
        // Legacy "visual_score" was RED/YELLOW/GREEN. 
        // We map this to a representative number for the ScorePill if needed, 
        // OR we just pass the semantic color if the ScorePill supports it.
        // The Charter says ScorePill takes a number OR a semantic string level?
        // Let's check ScorePill contract. If it only takes numbers, we might need a mapping.
        // However, ScorePill usually handles `visual_score` directly if we passed it in previous code.
        // Let's standardize on passing the raw value but ensuring it's cleaned.
        normalizedScore = entry.visual_score;
    }

    // 2. Recommendation Normalization
    // User Rec > Agent Rec > null
    let normalizedRec = null;
    if (entry.user_recommendation) {
        normalizedRec = entry.user_recommendation.toUpperCase();
    } else if (entry.save_recommendation?.decision) {
        normalizedRec = entry.save_recommendation.decision.toUpperCase();
    }

    // Ensure valid set
    if (!['SAVE', 'DISCARD', 'TRACK'].includes(normalizedRec)) {
        if (normalizedRec === 'REJECT') normalizedRec = 'DISCARD'; // Common alias
        else if (normalizedRec === 'ACCEPT') normalizedRec = 'SAVE'; // Common alias
        else normalizedRec = null; // Default to neutral if unknown
    }

    // 3. Status Normalization
    // Added 'Ingested' for V2/Agent compatibility
    const validStatuses = ['pending', 'accepted', 'rejected', 'ingested'];
    const status = validStatuses.includes(entry.status?.toLowerCase()) ? entry.status.toLowerCase() : 'pending';

    return {
        id: entry.id,
        title: entry.title || 'Untitled Entry',

        // Semantics (Pills)
        score: normalizedScore,
        recommendation: normalizedRec,
        status: status,

        // Content (V2 Priority: output_summary -> content_summary -> summary_text -> content_raw)
        summary: entry.output_summary || entry.content_summary || entry.summary_text || entry.content_raw || '',
        analysis: entry.accrezeo_analysis || '',
        rationale: entry.rationale || '',
        user_feedback: entry.user_feedback || '',

        // Metadata
        category: entry.category || 'Article',
        source: (entry.source && entry.source.startsWith('http')) ? entry.source : null, // Enforce safe protocol
        date: entry.date_published || null,
        tags: Array.isArray(entry.tags) ? entry.tags : [],

        // Raw (Locked for Editing, but available if needed for WriteForm initialization)
        _raw: entry
    };
};
